import { useI18n } from "@/hooks/useI18n";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useDrawers } from "@/hooks/useDrawers";
import { SEOHead } from "@/components/SEOHead";
import { ContactForm } from "@/components/ContactForm";
import { 
  MessageSquare, 
  FileText, 
  Phone,
  Mail,
  MapPin,
  Clock,
  CheckCircle
} from "lucide-react";

export default function Contact() {
  const { t } = useI18n();

  const contactMethods = [
    {
      icon: Mail,
      title: "Email",
      description: "sales@andean-bloom.com",
      action: t('contact.email'),
      href: "mailto:sales@andean-bloom.com"
    },
    {
      icon: MessageSquare,
      title: "WhatsApp",
      description: "Quick response via WhatsApp",
      action: t('contact.whatsapp'),
      href: "https://wa.me/593981931367?text=Hello%20Andean%20Bloom"
    },
    {
      icon: Phone,
      title: "Phone",
      description: "+593 98 193 1367",
      action: t('contact.phone'),
      href: "tel:+593981931367"
    }
  ];


  const responseGuarantees = [
    { metric: "24h", label: t('contact.guarantees.email'), description: t('contact.guarantees.email.desc') },
    { metric: "2h", label: t('contact.guarantees.whatsapp'), description: t('contact.guarantees.whatsapp.desc') },
    { metric: "48h", label: t('contact.guarantees.quote'), description: t('contact.guarantees.quote.desc') }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-andean text-andean-cream">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl md:text-4xl font-display font-light mb-6 tracking-wide">
              {t('page.contact.title')}
            </h1>
            <p className="text-lg md:text-xl mb-8 text-andean-cream/90 font-light leading-relaxed">
              {t('contact.hero.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Response Guarantees */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-xl md:text-2xl font-display font-light text-foreground mb-4 tracking-wide">
              {t('contact.guarantees.title')}
            </h2>
            <p className="text-muted-foreground">
              {t('contact.guarantees.subtitle')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {responseGuarantees.map((item, index) => (
              <div key={index} className="text-center">
                <div className="text-2xl md:text-3xl font-light text-andean-forest mb-2 tracking-wide">
                  {item.metric}
                </div>
                <div className="font-light text-foreground mb-1 tracking-wide">
                  {item.label}
                </div>
                <div className="text-sm text-muted-foreground">
                  {item.description}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-2xl md:text-3xl font-display font-light text-foreground mb-4 tracking-wide">
              {t('contact.touch.title')}
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              {t('contact.touch.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {contactMethods.map((method, index) => (
              <Card key={index} className="text-center hover:shadow-andean transition-all duration-300">
                <CardHeader>
                  <div className="w-16 h-16 bg-gradient-andean rounded-full flex items-center justify-center mx-auto mb-4">
                    <method.icon className="w-8 h-8 text-andean-cream" />
                  </div>
                  <CardTitle className="text-xl">{method.title}</CardTitle>
                  <CardDescription>{method.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button asChild className="w-full">
                    <a href={method.href} target="_blank" rel="noopener noreferrer">
                      {method.action}
                    </a>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 bg-muted/20">
        <div className="container mx-auto px-4">
          <ContactForm />
        </div>
      </section>

    </div>
  );
}